// src/components/InviteCodeAdminPanel.js
// ============================================================================
// INVITE CODE ADMIN PANEL - Generate, view, and manage invite codes.
// Embeds into AdminToolsScreen or AdminDashboardScreen.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { httpsCallable, getFunctions } from 'firebase/functions';

const functions = getFunctions();

// ============================================================================
// COLORS (matches AdminToolsScreen COLORS)
// ============================================================================
const COLORS = {
  bg: '#000000',
  card: '#111111',
  cardAlt: '#0a0a0a',
  border: '#1f1f1f',
  borderLight: '#2a2a2a',
  primary: '#FCD000',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  text: '#ffffff',
  textMuted: '#888888',
  textDim: '#555555',
};

export default function InviteCodeAdminPanel() {
  // --- State ---
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Generate form
  const [showForm, setShowForm] = useState(false);
  const [formLabel, setFormLabel] = useState('');
  const [formMaxRedemptions, setFormMaxRedemptions] = useState('10');
  const [formCustomCode, setFormCustomCode] = useState('');

  // Recently generated code (shown inline instead of alert)
  const [lastGeneratedCode, setLastGeneratedCode] = useState(null);
  const [copied, setCopied] = useState(false);

  // --- Load codes + stats ---
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [codesResult, statsResult] = await Promise.all([
        httpsCallable(functions, 'listInviteCodes')(),
        httpsCallable(functions, 'getBetaStats')(),
      ]);
      setCodes(codesResult.data.codes || []);
      setStats(statsResult.data);
    } catch (err) {
      console.error('Failed to load invite code data:', err);
      Alert.alert('Error', 'Failed to load invite codes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- Generate Code ---
  const handleGenerate = async () => {
    const maxR = parseInt(formMaxRedemptions, 10);
    if (isNaN(maxR) || maxR < 1 || maxR > 10000) {
      Alert.alert('Invalid', 'Max redemptions must be between 1 and 10,000.');
      return;
    }

    setGenerating(true);
    try {
      const generateFn = httpsCallable(functions, 'generateInviteCode');
      const result = await generateFn({
        maxRedemptions: maxR,
        label: formLabel.trim() || undefined,
        customCode: formCustomCode.trim() || null,
      });

      // Show inline banner instead of alert
      setLastGeneratedCode(result.data.code);
      setCopied(false);
      setShowForm(false);
      setFormLabel('');
      setFormMaxRedemptions('10');
      setFormCustomCode('');
      loadData();
    } catch (err) {
      const msg = err?.message || 'Failed to generate code.';
      Alert.alert('Error', msg);
    } finally {
      setGenerating(false);
    }
  };

  // --- Deactivate / Reactivate ---
  const handleToggleActive = async (codeId, currentlyActive) => {
    const action = currentlyActive ? 'deactivateInviteCode' : 'reactivateInviteCode';
    const label = currentlyActive ? 'Deactivate' : 'Reactivate';

    try {
      await httpsCallable(functions, action)({ codeId });
      loadData();
    } catch (err) {
      Alert.alert('Error', `Failed to ${label.toLowerCase()} code.`);
    }
  };

  // --- Copy code to clipboard ---
  const handleCopy = async (codeText, fromBanner = false) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(codeText);
        if (fromBanner) {
          setCopied(true);
        }
      }
    } catch {
      // Fallback: just show the code
      Alert.alert('Code', codeText);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingWrap}>
        <ActivityIndicator size="small" color={COLORS.primary} />
        <Text style={styles.loadingText}>Loading invite codes...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* --- Stats Row --- */}
      {stats && (
        <View style={styles.statsRow}>
          <StatBox label="Beta Users" value={stats.betaUsers} color={COLORS.success} />
          <StatBox label="Active Codes" value={stats.activeCodes} color={COLORS.primary} />
          <StatBox label="Redeemed" value={stats.totalRedemptions} color={COLORS.info} />
          <StatBox
            label="Capacity"
            value={`${stats.totalRedemptions}/${stats.totalCapacity}`}
            color={COLORS.warning}
          />
        </View>
      )}

      {/* --- Generate Button --- */}
      <TouchableOpacity
        style={styles.generateBtn}
        onPress={() => setShowForm(!showForm)}
      >
        <Ionicons
          name={showForm ? 'close-circle-outline' : 'add-circle-outline'}
          size={20}
          color="#000"
        />
        <Text style={styles.generateBtnText}>
          {showForm ? 'Cancel' : 'Generate New Code'}
        </Text>
      </TouchableOpacity>

      {/* --- Generate Form --- */}
      {showForm && (
        <View style={styles.formCard}>
          <Text style={styles.formTitle}>New Invite Code</Text>

          <Text style={styles.fieldLabel}>Label (internal)</Text>
          <TextInput
            style={styles.textInput}
            value={formLabel}
            onChangeText={setFormLabel}
            placeholder="e.g. SF6 Discord Drop"
            placeholderTextColor={COLORS.textDim}
          />

          <Text style={styles.fieldLabel}>Max Redemptions</Text>
          <TextInput
            style={styles.textInput}
            value={formMaxRedemptions}
            onChangeText={setFormMaxRedemptions}
            placeholder="10"
            placeholderTextColor={COLORS.textDim}
            keyboardType="number-pad"
          />

          <Text style={styles.fieldLabel}>Custom Code (optional)</Text>
          <TextInput
            style={styles.textInput}
            value={formCustomCode}
            onChangeText={(t) => setFormCustomCode(t.toUpperCase())}
            placeholder="e.g. GDC2026! (blank = auto-generated)"
            placeholderTextColor={COLORS.textDim}
            autoCapitalize="characters"
            maxLength={30}
          />

          <TouchableOpacity
            style={[styles.submitBtn, generating && { opacity: 0.6 }]}
            onPress={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <ActivityIndicator color="#000" size="small" />
            ) : (
              <Text style={styles.submitBtnText}>Generate Code</Text>
            )}
          </TouchableOpacity>
        </View>
      )}

      {/* --- Generated Code Banner --- */}
      {lastGeneratedCode && (
        <View style={styles.generatedBanner}>
          <View style={styles.generatedBannerHeader}>
            <View style={styles.generatedBannerTitleRow}>
              <Ionicons name="checkmark-circle" size={20} color={COLORS.success} />
              <Text style={styles.generatedBannerTitle}>Code Generated</Text>
            </View>
            <TouchableOpacity onPress={() => setLastGeneratedCode(null)}>
              <Ionicons name="close" size={20} color={COLORS.textMuted} />
            </TouchableOpacity>
          </View>
          <TouchableOpacity
            style={styles.generatedCodeRow}
            onPress={() => handleCopy(lastGeneratedCode, true)}
          >
            <Text style={styles.generatedCodeText}>{lastGeneratedCode}</Text>
            <View style={styles.copyBtn}>
              <Ionicons
                name={copied ? 'checkmark' : 'copy-outline'}
                size={16}
                color={copied ? COLORS.success : '#000'}
              />
              <Text style={styles.copyBtnText}>
                {copied ? 'Copied' : 'Copy'}
              </Text>
            </View>
          </TouchableOpacity>
        </View>
      )}

      {/* --- Codes List --- */}
      <Text style={styles.sectionTitle}>
        Invite Codes ({codes.length})
      </Text>

      {codes.length === 0 ? (
        <Text style={styles.emptyText}>No invite codes generated yet.</Text>
      ) : (
        codes.map((item) => (
          <CodeRow
            key={item.id}
            item={item}
            onToggle={() => handleToggleActive(item.id, item.isActive)}
            onCopy={() => handleCopy(item.code, false)}
          />
        ))
      )}
    </View>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function StatBox({ label, value, color }) {
  return (
    <View style={styles.statBox}>
      <Text style={[styles.statValue, { color }]}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );
}

function CodeRow({ item, onToggle, onCopy }) {
  const isFull = item.currentRedemptions >= item.maxRedemptions;
  const isExpired = item.expiresAt && new Date(item.expiresAt) < new Date();
  const isInactive = !item.isActive;

  let statusColor = COLORS.success;
  let statusLabel = 'Active';
  if (isInactive) {
    statusColor = COLORS.danger;
    statusLabel = 'Inactive';
  } else if (isFull) {
    statusColor = COLORS.warning;
    statusLabel = 'Full';
  } else if (isExpired) {
    statusColor = COLORS.textDim;
    statusLabel = 'Expired';
  }

  const createdDate = item.createdAt
    ? new Date(item.createdAt).toLocaleDateString()
    : '--';

  return (
    <View style={[styles.codeRow, isInactive && styles.codeRowInactive]}>
      <View style={styles.codeHeader}>
        <TouchableOpacity onPress={onCopy} style={styles.codeTextWrap}>
          <Text style={styles.codeText}>{item.code}</Text>
          <Ionicons name="copy-outline" size={14} color={COLORS.textMuted} />
        </TouchableOpacity>
        <View style={[styles.statusBadge, { borderColor: statusColor }]}>
          <View style={[styles.statusDot, { backgroundColor: statusColor }]} />
          <Text style={[styles.statusText, { color: statusColor }]}>
            {statusLabel}
          </Text>
        </View>
      </View>

      {item.label ? (
        <Text style={styles.codeLabel}>{item.label}</Text>
      ) : null}

      <View style={styles.codeMetaRow}>
        <Text style={styles.codeMeta}>
          {item.currentRedemptions}/{item.maxRedemptions} redeemed
        </Text>
        <Text style={styles.codeMeta}>Created {createdDate}</Text>
      </View>

      {/* Redemption progress bar */}
      <View style={styles.progressTrack}>
        <View
          style={[
            styles.progressFill,
            {
              width: `${Math.min((item.currentRedemptions / item.maxRedemptions) * 100, 100)}%`,
              backgroundColor: isFull ? COLORS.warning : COLORS.success,
            },
          ]}
        />
      </View>

      {/* Actions */}
      <View style={styles.codeActions}>
        <TouchableOpacity
          style={[
            styles.actionBtn,
            { borderColor: item.isActive ? COLORS.danger : COLORS.success },
          ]}
          onPress={onToggle}
        >
          <Ionicons
            name={item.isActive ? 'close-circle-outline' : 'checkmark-circle-outline'}
            size={14}
            color={item.isActive ? COLORS.danger : COLORS.success}
          />
          <Text
            style={[
              styles.actionBtnText,
              { color: item.isActive ? COLORS.danger : COLORS.success },
            ]}
          >
            {item.isActive ? 'Deactivate' : 'Reactivate'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    paddingBottom: 16,
  },
  loadingWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 16,
  },
  loadingText: {
    color: COLORS.textMuted,
    fontSize: 14,
  },

  // Stats
  statsRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  statBox: {
    flex: 1,
    backgroundColor: COLORS.card,
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '800',
  },
  statLabel: {
    fontSize: 11,
    color: COLORS.textMuted,
    marginTop: 2,
  },

  // Generate button
  generateBtn: {
    backgroundColor: COLORS.primary,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 16,
  },
  generateBtnText: {
    color: '#000',
    fontSize: 16,
    fontWeight: '700',
  },

  // Form
  formCard: {
    backgroundColor: COLORS.card,
    borderRadius: 10,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  formTitle: {
    color: COLORS.text,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  fieldLabel: {
    color: COLORS.textMuted,
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
    marginTop: 8,
  },
  textInput: {
    backgroundColor: COLORS.cardAlt,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 6,
    color: COLORS.text,
    fontSize: 14,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  submitBtn: {
    backgroundColor: COLORS.primary,
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 16,
  },
  submitBtnText: {
    color: '#000',
    fontSize: 15,
    fontWeight: '700',
  },

  // Codes list
  sectionTitle: {
    color: COLORS.text,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },

  // Generated code banner
  generatedBanner: {
    backgroundColor: '#0a1a0a',
    borderRadius: 10,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.success,
  },
  generatedBannerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  generatedBannerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  generatedBannerTitle: {
    color: COLORS.success,
    fontSize: 14,
    fontWeight: '700',
  },
  generatedCodeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#111111',
    borderRadius: 8,
    padding: 14,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  generatedCodeText: {
    color: COLORS.primary,
    fontSize: 22,
    fontWeight: '800',
    fontFamily: 'monospace',
    letterSpacing: 2,
  },
  copyBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: COLORS.primary,
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  copyBtnText: {
    color: '#000',
    fontSize: 13,
    fontWeight: '700',
  },
  emptyText: {
    color: COLORS.textDim,
    fontSize: 14,
    textAlign: 'center',
    padding: 20,
  },

  // Code row
  codeRow: {
    backgroundColor: COLORS.card,
    borderRadius: 8,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  codeRowInactive: {
    opacity: 0.6,
  },
  codeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  codeTextWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  codeText: {
    color: COLORS.primary,
    fontSize: 16,
    fontWeight: '700',
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  codeLabel: {
    color: COLORS.textMuted,
    fontSize: 13,
    marginBottom: 6,
  },
  codeMetaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  codeMeta: {
    color: COLORS.textDim,
    fontSize: 12,
  },

  // Progress bar
  progressTrack: {
    height: 4,
    backgroundColor: COLORS.border,
    borderRadius: 2,
    marginBottom: 10,
    overflow: 'hidden',
  },
  progressFill: {
    height: 4,
    borderRadius: 2,
  },

  // Actions
  codeActions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  actionBtnText: {
    fontSize: 12,
    fontWeight: '600',
  },
});